import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

// The URL is the source of truth, including dialogs. Read window.location when
// writing so several controls changed in one event compose instead of clobbering.
export function useRouteState<T extends string | boolean | number | null>(key: string, initial: T, allowed?: readonly T[], persistDefault = false): [T, Dispatch<SetStateAction<T>>] {
  const location = useLocation(), navigate = useNavigate()
  const decode = (params: URLSearchParams): T => {
    const raw = params.get(key)
    if (raw === null) return initial
    const value = (typeof initial === 'boolean' ? raw === '1' : typeof initial === 'number' ? Number(raw) : raw) as T
    return allowed && !allowed.includes(value) ? initial : value
  }
  const options = useRef({ initial, allowed, persistDefault }); options.current = { initial, allowed, persistDefault }
  const value = decode(new URLSearchParams(location.search))
  const set = useCallback<Dispatch<SetStateAction<T>>>(action => {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get(key), initial = options.current.initial
    const previous = (raw === null ? initial : typeof initial === 'boolean' ? raw === '1' : typeof initial === 'number' ? Number(raw) : raw) as T
    const next = typeof action === 'function' ? action(previous) : action
    if ((!options.current.persistDefault && next === initial) || next === null || next === '') params.delete(key)
    else params.set(key, typeof next === 'boolean' ? next ? '1' : '0' : String(next))
    const search = params.toString()
    if (search !== window.location.search.slice(1)) void navigate({ pathname: window.location.pathname, search: search ? '?' + search : '' })
  }, [key, navigate])
  return [value, set]
}

export function updateRoute(values: Record<string, string | null>) {
  // Navigation is dispatched through React Router by the app-level listener.
  window.dispatchEvent(new CustomEvent('bramble:navigate', { detail: values }))
}
